import { useApi } from '@/api';
import { ChartSkeleton, ChartTimeFilter } from '@/components';
import { SimpleChartDataset } from '@blockanalitica/ui/package/types/src/components/charts/SimpleChart';
import { CurrencyValue, BarChart, formatDatetime } from '@blockanalitica/ui';
import { useState, useCallback, useMemo } from 'react';
import { useTheme } from 'styled-components';
import { useChartPeriod } from '@/hooks';

export default function HistoricTabs() {
  const [chartDaysAgo, setChartDaysAgo] = useState('30');
  const [chartPeriod, updateChartConfigWithPeriod] =
    useChartPeriod(chartDaysAgo);
  const config = updateChartConfigWithPeriod();

  const [data, error] = useApi({
    url: '/sbe/historic-volume/',
    queryParams: {
      days_ago: chartDaysAgo
    }
  });

  if (error) {
    throw error;
  }

  const timeFilter = useMemo(
    () => (
      <ChartTimeFilter activeOption={chartDaysAgo} onChange={setChartDaysAgo} />
    ),
    [chartDaysAgo]
  );

  const theme = useTheme();

  const [datasets] = useMemo(() => {
    const datasets: SimpleChartDataset[] = [
      {
        xField: 'date',
        yField: 'buy_volume',
        label: 'Buy',
        backgroundColor: theme.colors.chartUp
      },
      {
        xField: 'date',
        yField: 'sell_volume',
        label: 'Sell',
        backgroundColor: theme.colors.chartDown
      }
    ];

    return [datasets];
  }, [theme.colors]);

  const valueFormatter = useMemo(() => {
    function formatValue(value: string) {
      return (
        <CurrencyValue
          value={Number(value)}
          options={{
            currency: 'USD'
          }}
        />
      );
    }

    return formatValue;
  }, []);

  const subValueFormatter = useCallback(
    (value: string) => (
      <>
        {chartPeriod.labelPrefix}
        {formatDatetime(value, { format: chartPeriod.dateFormat })}
      </>
    ),
    [chartPeriod]
  );

  if (data) {
    return (
      <BarChart
        key="buy_sell"
        data={data}
        datasets={datasets}
        valueFormatter={valueFormatter}
        timeFilter={timeFilter}
        config={config}
        subValueFormatter={subValueFormatter}
      />
    );
  }

  return <ChartSkeleton />;
}
